import { useState } from 'react';
import styles from '../styles/GalleryPage.module.css';
import { useJustifiedLayout } from '../hooks/useJustifiedLayout';

// ─────────────────────────────────────────────
// GalleryPage is a reusable component.
// Pass props from your router to configure each gallery:
//
//   <GalleryPage
//     title="Wildlife"
//     description="..."
//     filters={['All', 'Birds', 'Mammals', 'Insects']}
//     photos={WILDLIFE_PHOTOS}
//   />
//
// photos array shape:
//   { src, alt, filter, width, height }
// ─────────────────────────────────────────────

const GAP = 8;

// Grid shows a small thumbnail; the lightbox loads the full-size file.
// Thumbnails are built by scripts/photos.py into /Pictures/thumbs/.
const thumbOf = src => src.replace('/Pictures/', '/Pictures/thumbs/');

// If a thumbnail is missing (photo added without running the script),
// fall back to the full-size file instead of showing a broken image.
const fallbackToFull = src => e => {
  const img = e.currentTarget;
  if (!img.dataset.fallback) {
    img.dataset.fallback = '1';
    img.src = src;
  }
};

export default function GalleryPage({ title, description, filters = ['All'], photos = [] }) {
  const [activeFilter, setActiveFilter] = useState('All');
  const [lightbox, setLightbox] = useState(null);

  const visible = activeFilter === 'All'
    ? photos
    : photos.filter(p => p.filter === activeFilter);

  const { containerRef, rows } = useJustifiedLayout(visible, { targetRowHeight: 320, gap: GAP });

  return (
    <main>

      {/* ── PAGE HEADER ──────────────────────── */}
      <header className={styles['gallery-header']}>
        <div className='container'>
          <p className={`label ${styles['gallery-header__label']}`}>Portfolio</p>
          <h1 className={styles['gallery-header__title']}>{title}</h1>
          {description && (
            <p className={styles['gallery-header__desc']}>{description}</p>
          )}
        </div>
      </header>

      {/* ── FILTER BAR ───────────────────────── */}
      {filters.length > 1 && (
        <div className={styles['gallery-filters']}>
          <div className='container' style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {filters.map(f => (
              <button
                key={f}
                className={`${styles['gallery-filter']} ${activeFilter === f ? styles['gallery-filter--active'] : ''}`}
                onClick={() => setActiveFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── JUSTIFIED GRID ───────────────────── */}
      <div className={`container ${styles['gallery-grid']}`}>
      <div ref={containerRef}>
        {rows.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className={styles['gallery-row']}
            style={{ gap: GAP, marginBottom: GAP }}
          >
            {row.map((photo, colIndex) => {
              const globalIndex = rows.slice(0, rowIndex).reduce((sum, r) => sum + r.length, 0) + colIndex;
              return (
                <figure
                  key={photo.src}
                  className={styles['gallery-item']}
                  style={{ width: photo.computedWidth, height: photo.computedHeight, flex: '0 0 auto' }}
                  onClick={() => setLightbox(photo)}
                  role='button'
                  tabIndex={0}
                  aria-label={`View ${photo.alt}`}
                  onKeyDown={e => e.key === 'Enter' && setLightbox(photo)}
                >
                  <img
                    src={thumbOf(photo.src)}
                    onError={fallbackToFull(photo.src)}
                    alt={photo.alt}
                    loading={globalIndex < 6 ? 'eager' : 'lazy'}
                    width={photo.computedWidth}
                    height={photo.computedHeight}
                  />
                  {photo.caption && (
                    <figcaption className={styles['gallery-item__caption']}>
                      <span className={styles['gallery-item__caption-text']}>
                        {photo.caption}
                      </span>
                    </figcaption>
                  )}
                </figure>
              );
            })}
          </div>
        ))}
      </div>
      </div>

      {/* ── LIGHTBOX ─────────────────────────── */}
      {lightbox && (
        <div
          className={styles.lightbox}
          onClick={() => setLightbox(null)}
          role='dialog'
          aria-modal='true'
          aria-label={lightbox.alt}
        >
          <img
            className={styles.lightbox__img}
            src={lightbox.src}
            alt={lightbox.alt}
            onClick={e => e.stopPropagation()} // don't close when clicking image
          />
          <button
            className={styles.lightbox__close}
            onClick={() => setLightbox(null)}
            aria-label='Close lightbox'
          >
            Close
          </button>
        </div>
      )}

    </main>
  );
}
